'use client';

import * as signalR from '@microsoft/signalr';
import * as signalRMsgPack from '@microsoft/signalr-protocol-msgpack';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChatPannel } from '@wwe-portal/ui/features/chat';

import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import { useToast } from '@wwe-portal/ui/components/toast';
import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { chatLocalization } from '@wwe-portal/web/utils/play-game/localization';
import { useTranslations } from 'next-intl';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';

import { InfoTooltip } from '../../../../../packages/ui/components/tooltip/info-tooltip';
import { FacebookLogin } from '../auth/facebook-login';
import Link from 'next/link';
import { ScopelyLogin } from '../auth/scopely-login';
import { slugify } from '@wwe-portal/web/utils/string';

const ESTIMATED_SIZE = 80;
const RATE_LIMIT = 3000;
const INPUT_RATE_LIMIT = 300;
const RETRY_DELAYS = [0, 3000, 3000, 3000];
const SERVER_TIMEOUT = 30_000;
const BATCH_SIZE = 100;
const FETCH_NEW_MESSAGE_INTERVAL = 30_000;
const placeholderData = new Array(20).fill(undefined);

enum MessageFetchDirection {
  ASC = 'asc',
  DESC = 'desc',
}
type MessageFetchDirectionType = `${MessageFetchDirection}`;

interface Message {
  ts: number;
  userId: string;
  message: string;
  avatar: string;
  indexId?: string;
}

const parseMessage = (responseData: string[], currentUser: unknown) => {
  const item10 = responseData[10];
  const indexId = responseData[2];
  const ts = responseData[0];
  if (!item10) return undefined;
  try {
    const data = JSON.parse(item10);
    const text = chatLocalization(data.Message, data, currentUser);
    const message: Message = {
      ts: Number(ts),
      userId: data.UserName,
      message: text,
      avatar: data.Portrait ?? '/assets/images/common/the-rock.webp',
      indexId,
    };
    return message;
  } catch {
    return undefined;
  }
};

const FactionLink: FC<{ factionName: string; children: ReactNode }> = ({
  factionName,
  children,
}) => {
  if (factionName)
    return <Link href={`/faction/${slugify(factionName)}`}>{children}</Link>;
  return <>{children}</>;
};

const UserLink: FC<{ userId: string; children: ReactNode }> = ({
  userId,
  children,
}) => {
  if (userId)
    return <Link href={`/player/${slugify(userId)}`}>{children}</Link>;
  return <>{children}</>;
};

interface ChatInteractionProps {
  header: string;
  currentUser: {
    factionName?: string;
    factionJoinTime?: number;
    userId?: string;
    nickname?: string;
    profileImg?: string;
    factionId?: string;
    lobbyAccessToken?: string;
    chatCookies?: string;
  };
}

const CHAT_MESSAGE_MAX_LENGTH = Number(
  process.env.REACT_APP_CHAT_MAX_LENGTH || 256
);

const ChatInteraction: FC<ChatInteractionProps> = ({ header, currentUser }) => {
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [isExceedMessageLimit, setIsExceedMessageLimit] = useState(false);
  const t = useTranslations();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const [connected, setConnected] = useState(false);

  const messageContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const newMessageTsRef = useRef<number | null>(null);
  const [newMessages, setNewMessages] = useState<(Message | undefined)[]>([]);

  const fetchFactionMessages = useCallback(
    async (indexId: string, direction: MessageFetchDirectionType) => {
      if (!connectionRef.current) return [];
      const msgs = await connectionRef.current.invoke(
        'FetchFactionMessagesHistory_V2',
        indexId ?? '',
        String(BATCH_SIZE),
        direction
      );
      return msgs.map((data) => parseMessage(data, currentUser)) as (
        | Message
        | undefined
      )[];
    },
    [currentUser]
  );

  const fetchAllFactionMessages = useCallback(
    async (initialIndexId: string, direction: MessageFetchDirectionType) => {
      if (!connectionRef.current) return [];
      const result: (Message | undefined)[] = [];
      let isEnd = false;
      let indexId: string | undefined = initialIndexId;
      do {
        const msgs = await fetchFactionMessages(indexId, direction);
        isEnd = msgs.length < BATCH_SIZE;
        if (direction === MessageFetchDirection.ASC) {
          indexId = msgs[msgs.length - 1]?.indexId;
        }
        if (direction === MessageFetchDirection.DESC) {
          indexId = msgs[0]?.indexId;
        }
        result.push(...msgs);
      } while (!isEnd && indexId);
      if (direction === MessageFetchDirection.ASC) {
        return result.reverse();
      }
      return result;
    },
    [fetchFactionMessages]
  );

  const { data, fetchNextPage, fetchPreviousPage, hasNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['chat', { factionId: currentUser.factionId }],
      queryFn: async (context) => {
        if (!connectionRef.current) return [];
        const { indexId, direction } = context.pageParam ?? {
          indexId: '',
          direction: MessageFetchDirection.DESC,
        };

        if (direction === MessageFetchDirection.DESC) {
          return fetchFactionMessages(indexId, direction);
        }

        if (direction === MessageFetchDirection.ASC) {
          return fetchAllFactionMessages(indexId, direction);
        }
        return [];
      },
      getNextPageParam: (_, allPages) => {
        const lastPage = allPages[allPages.length - 1];
        if (!lastPage?.length) return undefined;
        return {
          indexId: lastPage[lastPage.length - 1]?.indexId,
          direction: 'desc',
        };
      },
      getPreviousPageParam: (_, allPages) => {
        const indexId = allPages.find((page) => page.length > 0)?.[0]?.indexId;
        return {
          indexId: indexId,
          direction: 'asc',
        };
      },
      initialPageParam: {
        indexId: '',
        direction: MessageFetchDirection.DESC,
      },
      enabled: connected,
      gcTime: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
    });

  const allHistoryMessages = useMemo(() => {
    if (isLoading || !connected) {
      return placeholderData;
    }
    const result: (Message | undefined)[] = [];
    if (data) {
      const mergedData = data.pages.flatMap((page) => page);
      result.push(...mergedData);
    }
    if (hasNextPage) {
      result.push(undefined);
    }

    return result;
  }, [data, hasNextPage, isLoading, connected]);

  const isNewMessageOutDated = useMemo(() => {
    const firstMessages = allHistoryMessages[0];
    const firstNewMessaegs = newMessages[0];
    if (!firstMessages || !firstNewMessaegs) return false;
    return firstMessages.ts >= firstNewMessaegs.ts;
  }, [allHistoryMessages, newMessages]);

  const allMessages = useMemo(() => {
    if (isNewMessageOutDated) return allHistoryMessages;
    return [...newMessages, ...allHistoryMessages];
  }, [allHistoryMessages, newMessages, isNewMessageOutDated]);

  // Clear new messages if they are out dated
  useEffect(() => {
    if (isNewMessageOutDated) {
      setNewMessages([]);
    }
  }, [isNewMessageOutDated]);

  const debounceFetchNextPage = useMemo(
    () =>
      debounce(() => fetchNextPage(), RATE_LIMIT, {
        leading: true,
        maxWait: RATE_LIMIT,
      }),
    [fetchNextPage]
  );

  const debounceFetchPreviousPage = useMemo(
    () =>
      debounce(() => fetchPreviousPage(), RATE_LIMIT, {
        leading: true,
        maxWait: RATE_LIMIT,
      }),
    [fetchPreviousPage]
  );

  const count = allMessages.length;
  const getItemKey = useCallback(
    (index: number) => allMessages[index]?.ts ?? index,
    [allMessages]
  );

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => messageContainerRef.current,
    estimateSize: () => ESTIMATED_SIZE,
    overscan: 5,
    getItemKey,
    onChange: useCallback(
      (instance: Virtualizer<HTMLDivElement, Element>) => {
        if (instance.isScrolling && newMessageTsRef.current) {
          newMessageTsRef.current = null;
          debounceFetchPreviousPage();
        }
      },
      [debounceFetchPreviousPage]
    ),
  });

  const debounceHandleChangeInput = useMemo(
    () =>
      debounce((value: string) => {
        const trimedValue = value.trim();
        const isExceed = Boolean(
          trimedValue && trimedValue.length > CHAT_MESSAGE_MAX_LENGTH
        );
        setButtonDisabled(!trimedValue || isExceed);
        setIsExceedMessageLimit(isExceed);
      }, INPUT_RATE_LIMIT),
    []
  );

  useEffect(() => {
    const timer = setInterval(() => {
      if (virtualizer.scrollOffset <= ESTIMATED_SIZE)
        debounceFetchPreviousPage();
    }, FETCH_NEW_MESSAGE_INTERVAL);
    return () => {
      clearInterval(timer);
    };
  }, [debounceFetchPreviousPage]);

  const toastChatError = useCallback(() => {
    toast({
      variant: 'error',
      content: t('something_went_wrong'),
    });
  }, [toast, t]);

  const sendMessage = async (text: string) => {
    if (!connectionRef.current) return;
    const now = new Date();
    try {
      await connectionRef.current.invoke('SendFactionMessage', [
        currentUser.userId,
        currentUser.nickname,
        text,
        '',
        '',
        '',
        currentUser.profileImg,
        false,
        '',
        now.toISOString(),
        now.getTime(),
        'Chat',
        'Positive',
        null,
      ]);
    } catch {
      toastChatError();
    }
  };

  useEffect(() => {
    const { lobbyAccessToken, factionId, factionJoinTime, chatCookies } =
      currentUser;
    if (!lobbyAccessToken || !factionId) return;

    const paramQuery = new URLSearchParams();
    paramQuery.set('factionId', factionId);
    paramQuery.set('faction_join_time', String(factionJoinTime));

    const url = `${process.env.NEXT_PUBLIC_CHATHUB_URL}?${paramQuery.toString()}`;

    try {
      // enable domain setter by add Origin-Agent-Cluster to response header
      document.domain = 'wwechampions.com';
    } catch {
      //
    }
    if (chatCookies && Array.isArray(chatCookies)) {
      chatCookies.forEach((item) => {
        const cookie = `${item.name}=${item.value}; path=${item.path}; domain=.wwechampions.com; expires=${item.expires}`;
        document.cookie = cookie;
      });
    }
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => lobbyAccessToken,
        withCredentials: true,
      })
      .configureLogging(
        process.env.REACT_APP_ENVIRONMENT !== 'prod'
          ? signalR.LogLevel.None
          : signalR.LogLevel.Trace
      )
      .withServerTimeout(SERVER_TIMEOUT)
      .withHubProtocol(new signalRMsgPack.MessagePackHubProtocol())
      .withStatefulReconnect()
      .withAutomaticReconnect(RETRY_DELAYS)
      .build();

    connection.on('ReceiveFactionMessage', (message) => {
      const nextMessage = parseMessage(message, currentUser);
      if (!nextMessage) return;
      if (virtualizer.scrollOffset <= ESTIMATED_SIZE) {
        setNewMessages((currentMessages) => [nextMessage, ...currentMessages]);
        return;
      }
      newMessageTsRef.current = nextMessage.ts;
    });

    connection
      .start()
      .then(() => {
        setConnected(true);
      })
      .catch((e) => {
        addDDError(e);
        console.error(e);
        toastChatError();
      });

    connectionRef.current = connection;

    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, [currentUser, toastChatError]);

  const handleSubmit = () => {
    const value = inputRef.current?.value?.trim();
    if (!value || value.length > CHAT_MESSAGE_MAX_LENGTH) return;

    sendMessage(value).catch((e) => {
      addDDError(e);
      toastChatError();
    });

    if (inputRef.current) {
      inputRef.current.value = '';
      debounceHandleChangeInput('');
    }
    virtualizer.scrollToIndex(0);
  };

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = items[items.length - 1];
    if (
      lastItem &&
      !allMessages[lastItem?.index] &&
      hasNextPage &&
      allMessages.length > 0
    ) {
      debounceFetchNextPage();
    }
  }, [hasNextPage, items, debounceFetchNextPage, allMessages]);
  const totalSize = virtualizer.getTotalSize();
  const measureElement = virtualizer.measureElement;

  return (
    <ChatPannel
      FactionLink={FactionLink}
      header={header}
      headerTooltip={<InfoTooltip content={t('chat_info')} />}
      factionName={currentUser.factionName}
      messageProps={
        allMessages.length
          ? {
              messages: allMessages as never,
              UserLink,
              items,
              totalSize,
              measureElement,
            }
          : undefined
      }
      unavailableProps={
        allMessages.length
          ? undefined
          : {
              title: t('faction_chat_loggedIn_no_messages_title'),
              description: t('faction_chat_loggedIn_no_messages_description'),
            }
      }
      inputProps={{
        inputRef,
        onSubmit: handleSubmit,
        disabled: !currentUser.factionName,
        buttonDisabled,
        onInputChange: (e) => debounceHandleChangeInput(e.target.value?.trim()),
        tooltipContent: isExceedMessageLimit
          ? t('faction_chat_input_exceedLimit', {
              max: CHAT_MESSAGE_MAX_LENGTH,
            })
          : undefined,
      }}
      messageListRef={messageContainerRef}
    />
  );
};

const chatQueryClient = new QueryClient();

export const ChatSection = () => {
  const currentUser = useGetCurrentUser();
  const t = useTranslations();

  const header = `${t('faction')} ${t('chat')}`;
  const disableInputProps = {
    disabled: true,
    buttonDisabled: true,
    onSubmit: () => {},
  };
  if (!currentUser) {
    return (
      <ChatPannel
        className="playgame-panel"
        FactionLink={FactionLink}
        header={header}
        headerTooltip={<InfoTooltip content={t('chat_info')} />}
        unavailableProps={{
          title: t('faction_chat_not_loggedIn_title'),
          description: t('faction_chat_not_loggedIn_description'),
          loginButtons: (
            <div className="chat-panel__fb-btn w-full px-0 md:px-8">
              <FacebookLogin className="w-full" />
              <div className="login-with-scopely mt-4">
                <ScopelyLogin className="w-full" />
              </div>
            </div>
          ),
        }}
        inputProps={disableInputProps}
      />
    );
  }

  if (!currentUser.factionName) {
    return (
      <ChatPannel
        className="playgame-panel"
        FactionLink={FactionLink}
        header={header}
        headerTooltip={<InfoTooltip content={t('chat_info')} />}
        factionName={currentUser.factionName}
        unavailableProps={{
          title: t('join_a_faction'),
          description: t('faction_chat_loggedIn_join_description'),
        }}
        inputProps={disableInputProps}
      />
    );
  }

  return (
    <QueryClientProvider client={chatQueryClient}>
      <ChatInteraction header={header} currentUser={currentUser} />
    </QueryClientProvider>
  );
};
